import { useState, useEffect } from 'react';
import {
  getPrograms,
  createProgram,
  updateProgram,
  deleteProgram,
  Program,
} from '../services/programsService';
import { formatDate } from '../utils/dateUtils';
import '../styles/FormPages.css';
import '../styles/ListPages.css';

export function ProgramsEditor() {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    programName: '',
    description: '',
  });

  useEffect(() => {
    loadPrograms();
  }, []);

  const loadPrograms = async () => {
    try {
      setLoading(true);
      const data = await getPrograms();
      setPrograms(data);
      setError('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load programs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (isEditing && editingId) {
        await updateProgram(editingId, formData);
        setSuccess('Program updated successfully');
      } else {
        await createProgram(formData);
        setSuccess('Program created successfully');
      }

      setFormData({ programName: '', description: '' });
      setIsEditing(false);
      setEditingId(null);
      loadPrograms();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save program');
    }
  };

  const handleEdit = (program: Program) => {
    setFormData({
      programName: program.programName,
      description: program.description || '',
    });
    setIsEditing(true);
    setEditingId(program.programID);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (id: number, name: string) => {
    if (
      !confirm(`Are you sure you want to delete "${name}"? This will hide it from all dropdowns.`)
    ) {
      return;
    }

    try {
      await deleteProgram(id);
      setSuccess('Program deleted successfully (soft delete)');
      loadPrograms();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete program');
    }
  };

  const handleCancel = () => {
    setFormData({ programName: '', description: '' });
    setIsEditing(false);
    setEditingId(null);
    setError('');
    setSuccess('');
  };

  return (
    <div>
      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
          {success}
        </div>
      )}

      {/* Add/Edit Form */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3>{isEditing ? 'Edit Program' : 'Add New Program'}</h3>
        <form onSubmit={handleSubmit} style={{ marginTop: '1rem' }}>
          <div className="form-row">
            <div className="form-group" style={{ flex: 2 }}>
              <label htmlFor="programName">Program Name *</label>
              <input
                type="text"
                id="programName"
                value={formData.programName}
                onChange={(e) => setFormData({ ...formData, programName: e.target.value })}
                placeholder="e.g., Food Pantry, Care Coordination"
                required
              />
            </div>

            <div className="form-group" style={{ flex: 3 }}>
              <label htmlFor="description">Description</label>
              <input
                type="text"
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this program"
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <button type="submit" className="button-primary">
              {isEditing ? 'Update Program' : 'Add Program'}
            </button>
            {isEditing && (
              <button type="button" className="button-secondary" onClick={handleCancel}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Programs List */}
      <div className="card">
        <h3>Current Programs</h3>
        {loading ? (
          <p>Loading...</p>
        ) : programs.length === 0 ? (
          <p style={{ color: '#999', fontStyle: 'italic' }}>No programs found.</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Program Name</th>
                  <th>Description</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {programs.map((program) => (
                  <tr key={program.programID}>
                    <td>
                      <strong>{program.programName}</strong>
                    </td>
                    <td>{program.description || '-'}</td>
                    <td>{formatDate(program.createdAt)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => handleEdit(program)}
                          className="button-link"
                          style={{ color: '#5B2C86' }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(program.programID, program.programName)}
                          className="button-link"
                          style={{ color: '#D32F2F' }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
